import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ProfileDropdown } from './ProfileDropdown';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { loadLocations } from '../lib/api';
import { 
  Eye
} from 'lucide-react';

export function HeadJudgeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTile, setSelectedTile] = useState<string>('penalty-logger'); // Penalty Logger selected by default
  const [activeTab, setActiveTab] = useState('penalties'); // Default tab for penalty logger
  const [userRole, setUserRole] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [stationName, setStationName] = useState<string>('');
  const [stationPenaltyTime, setStationPenaltyTime] = useState<number | null>(null);
  const [stationLocationId, setStationLocationId] = useState<number | null>(null);

  // Wall Balls: only Race Directors (roleid 3) may log penalties / DNF
  const wallBallActionsDisabled =
    stationLocationId === 11 && userRole !== 3;
  
  // Check user role and access permissions
  useEffect(() => {
    async function checkUserAccess() {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('raceofficial')
          .select('roleid')
          .eq('email', user.email)
          .eq('isactive', true)
          .single();

        if (error) {
          console.error('Error checking user access:', error);
          setIsLoading(false);
          return;
        }

        if (data && (data.roleid === 3 || data.roleid === 4 || data.roleid === 5)) {
          setUserRole(data.roleid);
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      } catch (err) {
        console.error('Error in checkUserAccess:', err);
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkUserAccess();
  }, [user?.email]);

  // Resolve saved station name from session (Set Station / header selector)
  useEffect(() => {
    async function resolveStationName() {
      const savedId = sessionStorage.getItem('currentLocation');
      if (!savedId) {
        setStationName('');
        setStationPenaltyTime(null);
        setStationLocationId(null);
        return;
      }

      const result = await loadLocations();
      if (result.success && result.data) {
        const match = result.data.find(
          (loc) => loc.locationid.toString() === savedId
        );
        setStationName(match?.locationname || '');
        setStationPenaltyTime(match?.penaltytime ?? null);
        setStationLocationId(match?.locationid ?? null);
      }
    }

    resolveStationName();
  }, []);

  // Auto-select tabs when tiles are selected
  React.useEffect(() => {
    if (selectedTile === 'penalty-logger') {
      setActiveTab('penalties');
    } else if (selectedTile === 'race-judges') {
      setActiveTab('judges');
    }
  }, [selectedTile]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="pen-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sw-yellow"></div>
          <p className="text-sw-muted text-xs font-extrabold uppercase tracking-wide">Checking access...</p>
        </div>
      </div>
    );
  }

  // Redirect if user doesn't have access
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="pen-screen">
      <header className="pen-topbar">
        <Link to="/dashboard" className="block hover:opacity-80 transition-opacity">
          <img
            src="/HyroxLogo.png"
            alt="Hyrox Logo"
            className="h-9 w-auto object-contain"
          />
        </Link>
        <div className="ml-auto">
          <ProfileDropdown variant="pen" />
        </div>
      </header>
      
      <div className="px-4 py-4 flex flex-col gap-3 max-w-lg mx-auto">
        <button
          type="button"
          className="pen-card hub-tile text-center cursor-pointer !p-3 flex items-center justify-center min-h-[120px]"
          onClick={() => setSelectedTile('penalty-logger')}
        >
          <img
            src="/icon-512x512.png"
            alt="HYROX"
            className="h-[88px] w-auto max-w-full object-contain"
          />
        </button>

        {stationName && (
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-sw-muted mb-1">
              Judging station
            </div>
            <h1 className="text-[40px] font-black uppercase tracking-[0.01em] text-white leading-[0.95] mb-1">
              {stationName}
            </h1>
          </div>
        )}

        <div className="w-[90%] mx-auto grid grid-cols-2 gap-3 mt-6">
          {/* Log Penalty — wireframe yellow tile */}
          <button
            type="button"
            disabled={wallBallActionsDisabled}
            title={
              wallBallActionsDisabled
                ? 'Log Penalty is only available to Race Directors at Wall Balls'
                : undefined
            }
            className={`min-h-[104px] rounded-sm border-2 border-black bg-sw-yellow text-black px-3.5 py-3.5 flex flex-col justify-between text-left ${
              wallBallActionsDisabled ? 'opacity-40 pointer-events-none' : ''
            }`}
            onClick={() => navigate('/penalty-logging')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" className="w-[26px] h-[26px]">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <div>
              <div className="text-[19px] font-black uppercase tracking-[0.01em] leading-[0.95]">
                Log<br />Penalty
              </div>
              <div className="text-[13px] font-black uppercase tracking-[0.02em] text-[#5a4a00] mt-1">
                {stationPenaltyTime != null ? `${stationPenaltyTime} SEC` : 'PRIMARY'}
              </div>
            </div>
          </button>

          {/* Mark DNF — wireframe red tile */}
          <button
            type="button"
            disabled={wallBallActionsDisabled}
            title={
              wallBallActionsDisabled
                ? 'Mark DQ/DNF is only available to Race Directors at Wall Balls'
                : undefined
            }
            className={`min-h-[104px] rounded-sm border-2 border-black bg-sw-red text-white px-3.5 py-3.5 flex flex-col justify-between text-left ${
              wallBallActionsDisabled ? 'opacity-40 pointer-events-none' : ''
            }`}
            onClick={() => navigate('/mark-dnf')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" className="w-[26px] h-[26px]">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
            <div>
              <div className="text-[19px] font-black uppercase tracking-[0.01em] leading-[0.95]">
                Mark<br />DQ/DNF
              </div>
              <div className="text-[13px] font-black uppercase tracking-[0.02em] text-[#ffd2d9] mt-1">
                Official
              </div>
            </div>
          </button>

          {/* Hold Time — review-queue green */}
          <button
            type="button"
            className="min-h-[104px] rounded-sm border-2 border-black bg-sw-green text-white px-3.5 py-3.5 flex flex-col justify-between text-left"
            onClick={() => navigate('/hold-time')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="w-[26px] h-[26px]">
              <circle cx="12" cy="13" r="8" />
              <path d="M12 9v4l2.5 2M9 3h6" strokeLinecap="round" />
            </svg>
            <div>
              <div className="text-[19px] font-black uppercase tracking-[0.01em] leading-[0.95]">
                Pause<br />Time
              </div>
              <div className="text-[13px] font-black uppercase tracking-[0.02em] text-[#c7f0cb] mt-1">
                Bonus
              </div>
            </div>
          </button>

          {/* Violations — wireframe yellow + red accent */}
          <button
            type="button"
            className="min-h-[104px] rounded-sm border-2 border-black bg-sw-yellow text-black px-3.5 py-3.5 flex flex-col justify-between text-left shadow-[inset_0_-10px_0_-4px_#fd655d]"
            onClick={() => navigate('/violations')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="w-[26px] h-[26px]">
              <path d="M12 3 2 20h20L12 3Z" />
              <path d="M12 10v4M12 17h.01" />
            </svg>
            <div>
              <div className="text-[19px] font-black uppercase tracking-[0.01em] leading-[0.95]">
                Violation
              </div>
              <div className="text-[13px] font-black uppercase tracking-[0.02em] text-[#5a4a00] mt-1">
                30 SEC
              </div>
            </div>
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate('/set-station')}
          className="pen-btn-ghost w-full !text-[11px] mt-6"
        >
          Change Station
        </button>

        {userRole === 3 && (
          <div className="pen-card mt-1">
            <div className="text-center text-base font-black tracking-[0.02em] uppercase text-white">
              Race Directors
            </div>
            <button
              type="button"
              className="pen-btn-primary w-full mt-3.5"
              onClick={() => navigate('/review-penalty')}
            >
              <Eye className="h-4 w-4" />
              Review Penalties
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
